import { ridership } from '@/endpoint.flexigo';
import Repository from './repository';
import { serializeParams } from '../helpers/utility';

const client = new Repository();

export default {
  /**
   * Get trip-level ridership data with stop details
   * @param {Object} params
   * @param {number} params.startDay - YYYYMMDD
   * @param {number} params.endDay - YYYYMMDD
   * @param {string} [params.direction] - ALL, INBOUND, OUTBOUND, LOOP
   * @param {Array<number>} [params.routeIds]
   * @param {string} [params.fromTime] - e.g. '06:00'
   * @param {string} [params.toTime] - e.g. '22:00'
   * @returns {Promise<Array>} - Array of RidershipTripDto
   */
  async getTrips(params) {
    const { startDay, endDay, direction, routeIds, fromTime, toTime, workingTypes } = params;
    const queryParams = { startDay, endDay };

    if (direction && direction !== 'ALL') {
      queryParams.direction = direction;
    }
    if (routeIds && routeIds.length > 0) {
      queryParams.routeIds = routeIds;
    }
    if (fromTime) {
      queryParams.fromTime = fromTime;
    }
    if (toTime) {
      queryParams.toTime = toTime;
    }
    if (workingTypes) {
      queryParams.workingTypes = workingTypes;
    }

    const response = await client.instance.get(ridership.trips, {
      params: queryParams,
      paramsSerializer: serializeParams,
    });
    return response.data;
  },

  /**
   * Get aggregated on/off counts per stop, broken down by route
   * @param {Object} params
   * @param {number} params.startDay - YYYYMMDD
   * @param {number} params.endDay - YYYYMMDD
   * @param {Array<number>} [params.routeIds]
   * @param {Array<number>} [params.stopIds]
   * @param {string} [params.fromTime] - e.g. '06:00'
   * @param {string} [params.toTime] - e.g. '22:00'
   * @returns {Promise<Array>} - Array of RidershipStopDto
   */
  async getStops(params) {
    const { startDay, endDay, routeIds, stopIds, fromTime, toTime, workingTypes } = params;
    const queryParams = { startDay, endDay };

    if (routeIds && routeIds.length > 0) {
      queryParams.routeIds = routeIds;
    }
    if (stopIds && stopIds.length > 0) {
      queryParams.stopIds = stopIds;
    }
    if (fromTime) {
      queryParams.fromTime = fromTime;
    }
    if (toTime) {
      queryParams.toTime = toTime;
    }
    if (workingTypes) {
      queryParams.workingTypes = workingTypes;
    }

    const response = await client.instance.get(ridership.stops, {
      params: queryParams,
      paramsSerializer: serializeParams,
    });
    return response.data;
  },

  /**
   * Get available routes for filter dropdown
   * @param {Object} params
   * @param {number} params.startDay - YYYYMMDD
   * @param {number} params.endDay - YYYYMMDD
   * @returns {Promise<Array>} - Array of RidershipRouteDto
   */
  async getRoutes(params) {
    const { startDay, endDay } = params;
    const response = await client.instance.get(ridership.routes, {
      params: { startDay, endDay },
    });
    return response.data;
  },

  /**
   * Get available stops for filter dropdown
   * @param {Object} params
   * @param {number} params.startDay - YYYYMMDD
   * @param {number} params.endDay - YYYYMMDD
   * @param {Array<number>} [params.routeIds]
   * @returns {Promise<Array>} - Array of RidershipStopListDto
   */
  async getStopsList(params) {
    const { startDay, endDay, routeIds } = params;
    const queryParams = { startDay, endDay };

    if (routeIds && routeIds.length > 0) {
      queryParams.routeIds = routeIds;
    }

    const response = await client.instance.get(ridership.stopsList, {
      params: queryParams,
      paramsSerializer: serializeParams,
    });
    return response.data;
  },

  /**
   * Export ridership data as Excel
   * @param {Object} params
   * @param {number} params.startDay - YYYYMMDD
   * @param {number} params.endDay - YYYYMMDD
   * @param {string} [params.direction] - ALL, INBOUND, OUTBOUND, LOOP
   * @param {Array<number>} [params.routeIds]
   * @returns {Promise<Blob>} - Excel binary
   */
  async exportExcel(params) {
    const { startDay, endDay, direction, routeIds } = params;
    const queryParams = { startDay, endDay };

    if (direction && direction !== 'ALL') {
      queryParams.direction = direction;
    }
    if (routeIds && routeIds.length > 0) {
      queryParams.routeIds = routeIds;
    }

    const response = await client.instance.get(ridership.exportExcel, {
      params: queryParams,
      paramsSerializer: serializeParams,
      responseType: 'blob',
    });
    return response.data;
  },
};
